import cytoscape, { Core } from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';
import dagre from 'cytoscape-dagre';
import { useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

import { useTheme } from '@/app/ThemeContext';
import { useOpenEntityDrawer } from '@/hooks/useOpenEntityDrawer';
import { transformGraphResponseToCyto } from '@/sections/insights/queryBuilder/components/QueryResults/utils';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import type { TreeNode } from '@/types';
import { cn } from '@/utils/classname';

// Register layouts
cytoscape.use(coseBilkent);
cytoscape.use(dagre);

/**
 * CoSE-Bilkent layout options for Cytoscape
 * Deterministic force-directed layout with compound node support
 */
interface CoseBilkentLayoutOptions {
  name: 'cose-bilkent';
  animate?: boolean | 'end';
  animationDuration?: number;
  fit?: boolean;
  padding?: number;
  // Node spacing
  idealEdgeLength?: number;
  nodeRepulsion?: number;
  nodeDimensionsIncludeLabels?: boolean;
  // Physics
  gravity?: number;
  gravityRange?: number;
  numIter?: number;
  // Tiling
  tile?: boolean;
  tilingPaddingVertical?: number;
  tilingPaddingHorizontal?: number;
  // Quality
  quality?: 'default' | 'draft' | 'proof';
  randomize?: boolean;
}

/**
 * Dagre layout options for Cytoscape
 * Hierarchical left-to-right layout
 */
interface DagreLayoutOptions {
  name: 'dagre';
  rankDir?: 'TB' | 'LR';
  nodeDimensionsIncludeLabels?: boolean;
  animate?: boolean;
}

interface GraphResultsProps {
  data: TreeNode[];
}

/**
 * GraphResults - Displays query results as an interactive Cytoscape graph
 *
 * Transforms TreeNode data to Cytoscape elements and renders interactive graph with zoom/pan
 */
export function GraphResults({ data }: GraphResultsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const { currentTheme } = useTheme();
  const { openDrawerByKey } = useOpenEntityDrawer();
  const { isGlobal, graphLayout, setGraphLayout } = useQueryBuilder();

  // Transform TreeNode data to Cytoscape elements with icons
  const cytoscapeElements = useMemo(() => {
    return transformGraphResponseToCyto(data, currentTheme);
  }, [data, currentTheme]);

  // Layout configurations
  const getLayoutConfig = (
    type: 'dagre' | 'cose-bilkent'
  ): DagreLayoutOptions | CoseBilkentLayoutOptions => {
    if (type === 'dagre') {
      return {
        name: 'dagre',
        rankDir: 'LR',
        nodeDimensionsIncludeLabels: true,
        animate: true,
      };
    }

    return {
      name: 'cose-bilkent',
      animate: 'end',
      animationDuration: 500,
      fit: true,
      padding: 30,
      idealEdgeLength: 150,
      nodeRepulsion: 6000,
      nodeDimensionsIncludeLabels: true,
      gravity: 0.25,
      gravityRange: 3.8,
      numIter: 2500,
      tile: true,
      tilingPaddingVertical: 40,
      tilingPaddingHorizontal: 40,
      quality: 'default',
      randomize: false,
    };
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Initialize Cytoscape
    const cy = cytoscape({
      container: containerRef.current,
      elements: cytoscapeElements,
      style: [
        {
          selector: 'node',
          style: {
            width: 72,
            height: 72,
            label: 'data(label)',
            color: currentTheme === 'light' ? '#1D1A3B' : '#e6eaff',
            'text-outline-width': currentTheme === 'light' ? 2 : 0,
            'text-outline-color':
              currentTheme === 'light' ? '#F3F0FF' : 'transparent',
            'font-size': 18,
            'text-halign': 'center',
            'text-valign': 'bottom',
            'text-margin-y': 8,
            'text-wrap': 'wrap',
            'text-max-width': '200px',
            'background-image': 'data(icon)',
            'background-fit': 'contain',
            'background-width': '32px',
            'background-height': '32px',
            'background-position-x': '50%',
            'background-position-y': '50%',
          },
        },
        {
          selector: 'node[key]:active',
          style: {
            'overlay-color': currentTheme === 'light' ? '#7A63FF' : '#8c7bff',
            'overlay-opacity': 0.2,
            'overlay-padding': 8,
          },
        },
        {
          selector: 'edge',
          style: {
            width: 3,
            'line-color': currentTheme === 'light' ? '#7A63FF' : '#8c7bff',
            'target-arrow-color':
              currentTheme === 'light' ? '#7A63FF' : '#8c7bff',
            'line-style': 'solid',
            'curve-style': 'bezier',
            'target-arrow-shape': 'triangle',
            'target-arrow-fill': 'filled',
            'arrow-scale': 2,
            label: 'data(label)',
            'font-size': 16,
            color: currentTheme === 'light' ? '#5642B7' : '#cdd4ff',
            'text-rotation': 'autorotate',
            'text-margin-y': -18,
          },
        },
      ],
      layout: getLayoutConfig(graphLayout),
      minZoom: 0.1,
      maxZoom: 3,
      wheelSensitivity: 1.0,
    });

    cyRef.current = cy;

    // Add click event handler for nodes with keys
    cy.on('click', 'node', evt => {
      if (isGlobal) {
        toast.error(
          'Table rows are not clickable in global query mode. Please assume into a specific account to review full item details.'
        );
        return;
      }

      const node = evt.target;

      let key = node.data('key');
      if (key && key.startsWith('#port')) {
        key = String(node.data('source'));
      } else if (key && key.startsWith('#webpage')) {
        key = '#webapplication#' + String(node.data('url'));
      }

      if (key) {
        openDrawerByKey(key);
      }
    });

    // Add cursor pointer when hovering over nodes with keys
    cy.on('mouseover', 'node[key]', () => {
      if (containerRef.current) {
        containerRef.current.style.cursor = 'pointer';
      }
    });

    cy.on('mouseout', 'node[key]', () => {
      if (containerRef.current) {
        containerRef.current.style.cursor = 'default';
      }
    });

    // Cleanup on unmount
    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [cytoscapeElements, currentTheme, graphLayout]); // Do NOT add openDrawerByKey to deps (critical performance issue)

  return (
    <div
      role="region"
      aria-label="Query results graph visualization"
      className="h-full w-full p-4"
    >
      <div className="relative h-full w-full">
        {/* Layout selector - positioned top-right */}
        <div className="absolute top-6 right-6 z-10 flex items-center gap-2">
          <label
            htmlFor="layout-selector"
            className="text-secondary text-sm font-medium"
          >
            Layout
          </label>
          <select
            id="layout-selector"
            value={graphLayout}
            onChange={e =>
              setGraphLayout(e.target.value as 'dagre' | 'cose-bilkent')
            }
            aria-label="Select graph layout algorithm"
            className="border-chariot-stroke bg-layer0 rounded border px-3 py-1.5 text-sm shadow-xs"
          >
            <option value="dagre">Left-to-Right</option>
            <option value="cose-bilkent">Clustered</option>
          </select>
        </div>

        {/* Graph container */}
        <div
          ref={containerRef}
          data-testid="cytoscape-graph"
          className={cn(
            'bg-chariot-background-secondary h-full w-full rounded-lg'
          )}
        />
      </div>
    </div>
  );
}
